import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const TIMELINE = [
  {
    era: "c. 1900",
    title: "A Child Named Lakshmi Narayan",
    text: "Maharaj-ji was born Lakshmi Narayan Sharma in the village of Akbarpur, in the Firozabad district of Uttar Pradesh, into a prosperous Brahmin family. Even as a child, he showed little interest in the world — and total absorption in God.",
  },
  {
    era: "Youth",
    title: "The Wandering Sadhu",
    text: "Married young by family tradition, he soon left home to wander the length of North India as a sadhu. Villagers knew him by many names — Talli Baba, Handi Wallah Baba — an unassuming blanket-wrapped saint whose miracles quietly followed him wherever he sat.",
  },
  {
    era: "The Miracle",
    title: "The Train at Neeb Karori",
    text: "Traveling without a ticket, Baba was put off the train by a conductor at the small station of Neeb Karori. The train would not move — engines failed, signals jammed — until the officials begged the sadhu to re-board. The moment he did, the train rolled on. The village gave him the name the world now knows: Neem Karoli Baba.",
  },
  {
    era: "1962 – 1964",
    title: "Ashrams of Vrindavan & Kainchi Dham",
    text: "Baba established his great ashrams — first at Vrindavan, then in 1964 at Kainchi Dham in the Kumaon foothills of the Himalayas, a valley he said was dear to Hanuman-ji himself. Over his lifetime he founded more than a hundred temples, most dedicated to Lord Hanuman.",
  },
  {
    era: "1967 – 1973",
    title: "The West Comes to Baba",
    text: "Western seekers — among them a young Harvard professor named Richard Alpert (Ram Dass) — found their way to Kainchi. Through 'Be Here Now' and the kirtans of Krishna Das, Baba's love crossed oceans. He asked for nothing, built no organization, gave no formal teachings — only love, food, and the name of Ram.",
  },
  {
    era: "Sept 11, 1973",
    title: "Mahasamadhi — and a Living Presence",
    text: "Baba left his body at Vrindavan in 1973. Yet devotees across the world testify that his presence never left them. His ashrams in India and his temples abroad — including this one in Denison, Texas — continue his one instruction: love everyone, serve everyone.",
  },
];

export default function BabaLifeStory() {
  return (
    <section id="story" data-testid="life-story-section" className="py-20 sm:py-28 bg-[#1C1917] text-stone-200 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #F59E0B 0%, transparent 40%), radial-gradient(circle at 80% 80%, #EA580C 0%, transparent 40%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow !text-amber-400 mb-4">03 · The Life of Maharaj-ji</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-100" data-testid="story-heading">
            The Saint Who Taught Only by Loving
          </h2>
          <p className="mt-5 text-stone-400 leading-relaxed">
            Neem Karoli Baba — Maharajji — wrote no books and gave no lectures. Devotees across the world
            revere him as a Hanuman Ansh, a living avatar of Lord Hanuman. He simply loved — and in that love,
            thousands found God. This is his story.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="lg:sticky lg:top-28"
          >
            <div className="diya-glow rounded-2xl border border-amber-100/10 bg-gradient-to-b from-amber-100 to-[#F5F0E6] p-6 sm:p-8">
              <img src="/images/baba-portrait-2.png" alt="Neem Karoli Baba (Maharaj-ji) in his beloved blanket"
                data-testid="story-baba-portrait"
                className="relative z-10 mx-auto w-full max-w-[260px] rounded-xl shadow-2xl shadow-amber-900/30 float-slow" />
              <p className="relative z-10 text-center mt-5 font-quote italic text-xl text-amber-950">Neem Karoli Baba</p>
              <p className="relative z-10 text-center text-[11px] uppercase tracking-[0.25em] text-amber-700/80 mt-1">Maharaj-ji · c. 1900 – 1973</p>
            </div>
            <div className="mt-6 rounded-2xl overflow-hidden border border-amber-100/10">
              <img src="/images/devotee-gathering.webp" alt="Devotees gathered around Baba's garlanded portrait at the Denison mandir"
                data-testid="story-gathering-image" className="w-full aspect-[4/3] object-cover" />
            </div>
            <p className="mt-4 text-xs text-stone-500 tracking-wide flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" /> Satsang at our Denison mandir — Baba's portrait, fresh garlands, and family.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-amber-500/60 via-amber-500/25 to-transparent" />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <motion.div key={t.title}
                  initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.05 * i }}
                  data-testid={`timeline-item-${i}`}
                  className="relative pl-10"
                >
                  <span className="absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full border-2 border-amber-500 bg-[#1C1917]" />
                  <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">{t.era}</p>
                  <h3 className="font-display text-lg sm:text-xl text-amber-100 mt-1.5">{t.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-stone-400">{t.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
